import { useState } from "react";
import { CANVAS_TRASHED_EDIT_MESSAGE } from "@refinement-canvas/domain";
import { useCanvasApi } from "../../../lib/canvas-api";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useShowToast } from "../notice/use-show-toast";

interface Props {
  readonly canvasId: string;
  readonly canWrite: boolean;
}

/** ゴミ箱キャンバスを参照専用で開いていることと、復元手段を常設表示する。 */
export function TrashedCanvasBanner({ canvasId, canWrite }: Props): JSX.Element | null {
  const isTrashed = useCanvasDocStore((state) => state.meta.isTrashed ?? false);
  const canvasApi = useCanvasApi();
  const showToast = useShowToast();
  const [restoring, setRestoring] = useState(false);

  if (!isTrashed) return null;

  const handleRestore = async (): Promise<void> => {
    if (restoring) return;
    setRestoring(true);
    try {
      await canvasApi.restoreCanvas(canvasId);
      window.location.reload();
    } catch {
      showToast("warning", "ゴミ箱から戻せませんでした。時間をおいて再度お試しください。");
      setRestoring(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="trashed-canvas-banner"
      style={{
        position: "fixed",
        top: 8,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 900,
        maxWidth: "min(560px, calc(100vw - 32px))",
        padding: "8px 12px",
        border: "1px solid var(--color-warning)",
        borderRadius: "var(--radius-2)",
        background: "var(--color-surface)",
        boxShadow: "var(--shadow-2)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span>{CANVAS_TRASHED_EDIT_MESSAGE}</span>
      {canWrite ? (
        <button type="button" disabled={restoring} onClick={() => void handleRestore()}>
          ゴミ箱から戻す
        </button>
      ) : null}
      <button type="button" onClick={() => window.location.reload()}>
        再読み込み
      </button>
    </div>
  );
}
